import React, { useState, useEffect, useRef } from 'react';

function BubbleMapResults({
  cityData,
  isVisible,
  searchOrigin,
  onCityClick,
  onSetOrigin,
  displayMode,
  onDisplayModeChange,
  mapBackground = 'streets',
  formatPaymentForBubble,
  getPaymentColor,
  formatNumber,
  formatCurrency,
  newLoads = [],
  showingNewLoads = false,
  clearRoutes = 0,
  showCommentary = false,
  bestRatedLane = null,
  isMapFullscreen = false
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const currentPopupRef = useRef(null);
  const routePopupRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Map background style mapping
  const getMapboxStyle = (background) => {
    const styles = {
      'light': 'mapbox://styles/mapbox/light-v11',
      'satellite': 'mapbox://styles/mapbox/satellite-v9',
      'dark': 'mapbox://styles/mapbox/dark-v11', 
      'streets': 'mapbox://styles/mapbox/streets-v12'
    };
    return styles[background] || styles['light'];
  };

  // Format rate per mile for bubble display
  const formatRateForBubble = (rate) => {
    return `$${rate.toFixed(2)}`;
  };

  // Get color based on rate per mile (REVERSED: Green for high, red for low)
  const getRateColor = (rate) => {
    if (rate >= 4.0) return '#16a34a'; // Green - High rate
    if (rate >= 3.5) return '#65a30d'; // Green-yellow
    if (rate >= 3.0) return '#ca8a04'; // Yellow-orange
    if (rate >= 2.5) return '#d97706'; // Orange
    if (rate >= 2.0) return '#ea580c'; // Orange-red
    return '#dc2626'; // Red - Lower rate
  };

  // Format price difference for bubble display
  const formatPriceDiffForBubble = (priceDiff) => {
    if (priceDiff === null || priceDiff === undefined || isNaN(priceDiff)) {
      return 'N/A';
    }
    const sign = priceDiff > 0 ? '+' : '';
    return `${sign}${formatCurrency(priceDiff)}`;
  };

  // Get color based on price difference with dynamic scaling
  const getPriceDiffColor = (priceDiff, minValue = null, maxValue = null) => {
    if (priceDiff === null || priceDiff === undefined || isNaN(priceDiff)) {
      return '#6b7280'; // Gray for N/A
    }

    // Fair pricing zone: ±$100 = white
    if (Math.abs(priceDiff) <= 100) {
      return '#f8fafc'; // Very light gray/white for fair priced
    }

    // Use dynamic scaling if min/max provided, otherwise use static thresholds
    if (minValue !== null && maxValue !== null) {
      const absMin = Math.abs(minValue);
      const absMax = Math.abs(maxValue);
      const localMax = Math.max(absMin, absMax);

      // Normalize the price difference relative to local range
      const normalizedValue = Math.abs(priceDiff) / localMax;

      if (priceDiff > 100) {
        // Positive values (underpriced) - shades of green
        // Intensity based on how far from fair pricing zone relative to local max
        const intensity = (priceDiff - 100) / (maxValue - 100);
        const clampedIntensity = Math.min(Math.max(intensity, 0), 1);

        if (clampedIntensity > 0.8) return '#15803d'; // Very dark green
        if (clampedIntensity > 0.6) return '#16a34a'; // Dark green
        if (clampedIntensity > 0.4) return '#22c55e'; // Medium green
        if (clampedIntensity > 0.2) return '#4ade80'; // Light green
        return '#86efac'; // Very light green
      } else {
        // Negative values (overpriced) - shades of red
        const intensity = (Math.abs(priceDiff) - 100) / (Math.abs(minValue) - 100);
        const clampedIntensity = Math.min(Math.max(intensity, 0), 1);

        if (clampedIntensity > 0.8) return '#dc2626'; // Very dark red
        if (clampedIntensity > 0.6) return '#e11d48'; // Dark red
        if (clampedIntensity > 0.4) return '#f97316'; // Orange-red
        if (clampedIntensity > 0.2) return '#fb923c'; // Light orange
        return '#fed7aa'; // Very light orange
      }
    }

    // Fallback to static thresholds if no range provided
    if (priceDiff > 200) return '#16a34a'; // Dark green - Very underpriced
    if (priceDiff > 100) return '#22c55e'; // Green - Underpriced
    if (priceDiff > -100) return '#eab308'; // Yellow - Slightly overpriced
    if (priceDiff > -200) return '#f97316'; // Orange - Overpriced
    return '#dc2626'; // Red - Very overpriced
  };

  // Format profit for bubble display
  const formatProfitForBubble = (profit) => {
    if (profit >= 1000) return `$${(profit / 1000).toFixed(1)}k`;
    if (profit >= 0) return `$${Math.round(profit)}`;
    return `-$${Math.round(Math.abs(profit))}`;
  };

  // Get color based on profit amount (REVERSED: Green for high, red for low)
  const getProfitColor = (profit) => {
    if (profit >= 1000) return '#16a34a'; // Green - High profit
    if (profit >= 750) return '#65a30d'; // Green-yellow
    if (profit >= 500) return '#ca8a04'; // Yellow-orange
    if (profit >= 250) return '#d97706'; // Orange
    if (profit >= 100) return '#ea580c'; // Orange-red
    if (profit >= 0) return '#dc2626'; // Red - Low but positive profit
    return '#6b7280'; // Gray - Negative profit
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Clear route layers and sources from map
  const clearRoute = (map) => {
    try {
      // Clear route layers and sources
      if (map.getLayer('route-label')) {
        map.removeLayer('route-label');
      }
      if (map.getLayer('route')) {
        map.removeLayer('route');
      }
      if (map.getSource('route-label')) {
        map.removeSource('route-label');
      }
      if (map.getSource('route')) {
        map.removeSource('route');
      }

      // Clear route popup if it exists
      if (routePopupRef.current) {
        routePopupRef.current.remove();
        routePopupRef.current = null;
      }

      // Clear persistent load detail popups on new search
      if (currentPopupRef.current) {
        currentPopupRef.current.remove();
        currentPopupRef.current = null;
        console.log('🗑️ Closed persistent popup due to new search');
      }

      // NOTE: Commentary markers are NOT cleared here intentionally
      // They should persist across route changes and only be updated when commentary toggle changes

      console.log('🗺️ Route cleared by search action');
    } catch (error) {
      console.log('No existing route to clear:', error);
    }
  };

  // Add commentary markers to map
  const addCommentaryMarkers = (map) => {
    try {
      // Clear existing commentary layers
      if (map.getLayer('best-rated-marker')) {
        map.removeLayer('best-rated-marker');
      }
      if (map.getSource('best-rated-marker')) {
        map.removeSource('best-rated-marker');
      }

      if (!showCommentary || !bestRatedLane) return;

      console.log('🏆 Adding Best Rated marker for:', bestRatedLane.cityState, 'Price Diff: +$' + bestRatedLane.avgPriceDiff.toFixed(0));

      // Add best rated lane marker
      map.addSource('best-rated-marker', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [bestRatedLane.lon, bestRatedLane.lat]
          },
          properties: {
            title: '⭐ Best Rated',
            description: `${bestRatedLane.cityState}\nPrice Difference: +$${bestRatedLane.avgPriceDiff.toFixed(0)}\nAvg Payment: $${bestRatedLane.avgPayment.toFixed(0)}`
          }
        }
      });

      // Add star marker symbol layer
      map.addLayer({
        id: 'best-rated-marker',
        type: 'symbol',
        source: 'best-rated-marker',
        layout: {
          'icon-image': 'custom-star',
          'icon-size': 1.0,
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
          'text-field': ['get', 'title'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-offset': [0, -2.5],
          'text-anchor': 'bottom',
          'text-size': 16
        },
        paint: {
          'text-color': '#2563eb', // Blue color for better readability
          'text-halo-color': '#ffffff',
          'text-halo-width': 2
        }
      });

      // Add click handler for commentary marker
      map.on('click', 'best-rated-marker', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const description = e.features[0].properties.description;

        new window.mapboxgl.Popup({ closeButton: true, closeOnClick: true })
          .setLngLat(coordinates)
          .setHTML(`
            <div style="font-family: system-ui, -apple-system, sans-serif;">
              <h3 style="margin: 0 0 8px 0; color: #2563eb; font-weight: 600; font-size: 16px;">⭐ Best Rated Lane</h3>
              <p style="margin: 0; white-space: pre-line; font-size: 14px; line-height: 1.4; color: #374151;">${description}</p>
            </div>
          `)
          .addTo(map);
      });

      // Add star icon if it doesn't exist
      if (!map.hasImage('custom-star')) {
        // Create a canvas for the star icon
        const size = 40;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // Draw star shape
        ctx.fillStyle = '#16a34a'; // Green color
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;

        const centerX = size / 2;
        const centerY = size / 2;
        const radius = 15;

        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        map.addImage('custom-star', canvas);
      }

    } catch (error) {
      console.error('Error adding commentary markers:', error);
    }
  };

  // Get coordinates from city name using Mapbox Geocoding API
  const getCityCoordinates = async (cityState) => {
    if (!cityState) {
      return [-95.7129, 37.0902]; // Default center of US
    }

    try {
      const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
      if (!MAPBOX_ACCESS_TOKEN) {
        console.warn('No Mapbox token available, using default coordinates');
        return [-95.7129, 37.0902];
      }

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(cityState)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=US&types=place&limit=1`
      );
      
      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const coordinates = data.features[0].center; // [lng, lat]
        console.log(`📍 Geocoded coordinates for ${cityState}:`, coordinates);
        return coordinates;
      }
      
      console.warn(`No coordinates found for ${cityState}, using default`);
      return [-95.7129, 37.0902];
      
    } catch (error) {
      console.error('Error geocoding city:', error);
      
      // Fallback to common US cities coordinates for quick lookup
      const cityCoords = {
        'Chicago, IL': [-87.6298, 41.8781],
        'Los Angeles, CA': [-118.2437, 34.0522],
        'New York, NY': [-74.0059, 40.7128],
        'Houston, TX': [-95.3698, 29.7604],
        'Phoenix, AZ': [-112.0740, 33.4484],
        'Philadelphia, PA': [-75.1652, 39.9526],
        'San Antonio, TX': [-98.4936, 29.4241],
        'San Diego, CA': [-117.1611, 32.7157],
        'Dallas, TX': [-96.7970, 32.7767],
        'San Jose, CA': [-121.8863, 37.3382],
        'Austin, TX': [-97.7431, 30.2672],
        'Jacksonville, FL': [-81.6557, 30.3322],
        'Fort Worth, TX': [-97.3301, 32.7555],
        'Columbus, OH': [-82.9988, 39.9612],
        'Indianapolis, IN': [-86.1581, 39.7684],
        'Charlotte, NC': [-80.8431, 35.2271],
        'San Francisco, CA': [-122.4194, 37.7749],
        'Seattle, WA': [-122.3321, 47.6062],
        'Denver, CO': [-104.9903, 39.7392],
        'Washington, DC': [-77.0369, 38.9072],
        'Boston, MA': [-71.0589, 42.3601],
        'El Paso, TX': [-106.4850, 31.7619],
        'Detroit, MI': [-83.0458, 42.3314],
        'Nashville, TN': [-86.7816, 36.1627],
        'Portland, OR': [-122.6784, 45.5152],
        'Memphis, TN': [-90.0490, 35.1495],
        'Oklahoma City, OK': [-97.5164, 35.4676],
        'Las Vegas, NV': [-115.1398, 36.1699],
        'Louisville, KY': [-85.7585, 38.2527],
        'Baltimore, MD': [-76.6122, 39.2904],
        'Milwaukee, WI': [-87.9065, 43.0389],
        'Atlanta, GA': [-84.3880, 33.7490],
        'Miami, FL': [-80.1918, 25.7617],
        'Kansas City, MO': [-94.5786, 39.0997],
        'Tampa, FL': [-82.4572, 27.9506],
        'Cleveland, OH': [-81.6944, 41.4993],
        'New Orleans, LA': [-90.0715, 29.9511],
        'Minneapolis, MN': [-93.2650, 44.9778]
      };

      // Return fallback coordinates if found, otherwise default to center of US
      return cityCoords[cityState] || [-95.7129, 37.0902];
    }
  };

  // Add driving route to map using actual coordinates
  const addDrivingRoute = async (map, originCoords, destCoords, destName) => {
    console.log('🚗 Adding driving route to', destName, 'at', destCoords);
    
    // Clear existing route if any
    try {
      if (map.getLayer('route-label')) {
        map.removeLayer('route-label');
      }
      if (map.getLayer('route')) {
        map.removeLayer('route');
      }
      if (map.getSource('route-label')) {
        map.removeSource('route-label');
      }
      if (map.getSource('route')) {
        map.removeSource('route');
      }
    } catch (e) {
      console.log('No existing route to clear');
    }
    
    // Use Mapbox Directions API with actual coordinates
    const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${originCoords[0]},${originCoords[1]};${destCoords[0]},${destCoords[1]}?geometries=geojson&access_token=${process.env.REACT_APP_MAPBOX_PUBLIC_KEY}`;
    
    try {
      const response = await fetch(directionsUrl);
      const data = await response.json();
      
      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        
        // Add route source and layer
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              distance: (route.distance / 1609.34).toFixed(1), // Convert to miles
              duration: Math.round(route.duration / 60) // Convert to minutes
            },
            geometry: route.geometry
          }
        });
        
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 4,
            'line-opacity': 0.75
          }
        }, 'city-bubbles'); // Add below city bubbles
        
        // Add route info as a symbol layer along the route
        const midpoint = route.geometry.coordinates[Math.floor(route.geometry.coordinates.length / 2)];
        
        map.addSource('route-label', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: midpoint
            },
            properties: {
              title: `${Math.round(route.distance / 1609.34)} mi • ${Math.floor(route.duration / 3600)}h ${Math.floor((route.duration % 3600) / 60)}m`
            }
          }
        });
        
        map.addLayer({
          id: 'route-label',
          type: 'symbol',
          source: 'route-label',
          layout: {
            'text-field': ['get', 'title'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 14,
            'text-anchor': 'center',
            'text-allow-overlap': true
          },
          paint: {
            'text-color': '#1e40af',
            'text-halo-color': '#ffffff',
            'text-halo-width': 2
          }
        })
        
        console.log(`✅ Route added: ${Math.round(route.distance / 1609.34)} miles, ${Math.round(route.duration / 60)} minutes`);
        
        // Don't change zoom - user requested to keep current view
        // const bounds = new window.mapboxgl.LngLatBounds();
        // route.geometry.coordinates.forEach(coord => bounds.extend(coord));
        // map.fitBounds(bounds, { padding: 50 });
        
        return {
          distance: Math.round(route.distance / 1609.34),
          duration: Math.round(route.duration / 60) // Keep in minutes for any other uses
        };
      }
    } catch (error) {
      console.error('Error fetching driving route:', error);
    }
    
    return null;
  };

  // Add city bubbles to map
  const addCityBubbles = (map, cities, searchOrigin) => {
    console.log('🎯 Adding', cities.length, 'city bubbles to map');
    console.log('🏷️ First city displayValue:', cities[0]?.avgPayment, cities[0]?.avgRate, cities[0]?.avgProfit);
    
    // Clear existing city layers and sources only (don't touch origin marker or route)
    try {
      // Remove layers first (order matters)
      if (map.getLayer('city-labels')) {
        map.removeLayer('city-labels');
      }
    } catch (e) {
      console.log('No city-labels layer to remove:', e.message);
    }
    
    try {
      if (map.getLayer('city-bubbles')) {
        map.removeLayer('city-bubbles');
      }
    } catch (e) {
      console.log('No city-bubbles layer to remove:', e.message);
    }
    
    try {
      // Then remove sources
      if (map.getSource('city-bubbles')) {
        map.removeSource('city-bubbles');
      }
    } catch (e) {
      console.log('No city-bubbles source to remove:', e.message);
    }

    // Sort cities by importance (load count) for prioritized labeling
    const sortedCities = [...cities].sort((a, b) => b.loadCount - a.loadCount);

    // Calculate price difference range for dynamic color scaling
    let priceDiffMin = null, priceDiffMax = null;
    if (displayMode === 'pricediff') {
      const validPriceDiffs = sortedCities
        .map(city => city.avgPriceDiff)
        .filter(diff => diff !== null && diff !== undefined && !isNaN(diff));

      if (validPriceDiffs.length > 0) {
        priceDiffMin = Math.min(...validPriceDiffs);
        priceDiffMax = Math.max(...validPriceDiffs);
        console.log('📊 Price Diff Range for dynamic scaling:', {
          min: priceDiffMin,
          max: priceDiffMax,
          fairZone: '±$100 = white'
        });
      }
    }

    // Create GeoJSON features for city bubbles based on display mode
    const features = sortedCities.map((city, index) => {
      let displayValue, color;

      switch (displayMode) {
        case 'rate':
          displayValue = formatRateForBubble(city.avgRate);
          color = getRateColor(city.avgRate);
          break;
        case 'profit':
          displayValue = formatProfitForBubble(city.avgProfit);
          color = getProfitColor(city.avgProfit);
          break;
        case 'pricediff':
          displayValue = formatPriceDiffForBubble(city.avgPriceDiff);
          color = getPriceDiffColor(city.avgPriceDiff, priceDiffMin, priceDiffMax);
          break;
        case 'payment':
        default:
          displayValue = formatPaymentForBubble(city.avgPayment);
          color = getPaymentColor(city.avgPayment);
          break;
      }
      
      // Check if this city has new loads
      const hasNewLoads = newLoads.some(load => {
        const loadCity = `${load.DestinationCity}, ${load.DestinationState}`;
        return loadCity === city.cityState;
      });
      
      // Modify color and opacity based on live loadboard mode
      let finalColor = color;
      let opacity = 0.8;
      
      if (showingNewLoads) {
        if (hasNewLoads) {
          // New loads: bright green with pulse effect
          finalColor = '#10b981'; // Bright green
          opacity = 1.0;
        } else {
          // Existing loads: grayed out
          opacity = 0.3;
        }
      }
      
      // Assign priority for label display (higher load count = higher priority)
      const labelPriority = sortedCities.length - index;
      
      // Debug logging
      if (index === 0) {
        console.log('🏷️ First city display value:', displayValue, 'for', city.cityState);
      }
      
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [city.lon, city.lat]
        },
        properties: {
          cityState: city.cityState,
          city: city.city,
          state: city.state,
          avgPayment: city.avgPayment,
          totalPayment: city.totalPayment,
          loadCount: city.loadCount,
          avgRate: city.avgRate,
          avgDistance: city.avgDistance,
          avgProfit: city.avgProfit,
          totalProfit: city.totalProfit,
          avgPriceDiff: city.avgPriceDiff,
          displayValue: displayValue,
          color: finalColor,
          opacity: opacity,
          labelPriority: labelPriority,
          hasNewLoads: hasNewLoads,
          isNewLoad: showingNewLoads && hasNewLoads
        }
      };
    });

    // Add source only if it doesn't already exist
    if (!map.getSource('city-bubbles')) {
      map.addSource('city-bubbles', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: features
        }
      });
    } else {
      // Update existing source data
      console.log('🔄 Updating existing city bubbles source data');
      map.getSource('city-bubbles').setData({
        type: 'FeatureCollection',
        features: features
      });
    }

    // Add bubble layer only if it doesn't already exist
    if (!map.getLayer('city-bubbles')) {
      map.addLayer({
        id: 'city-bubbles',
        type: 'circle',
        source: 'city-bubbles',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'loadCount'],
            1, 20,      // Min size: 20px
            10, 35,     // 10 loads: 35px  
            25, 50,     // 25 loads: 50px
            50, 65      // 50+ loads: 65px
          ],
          'circle-color': ['get', 'color'],
          'circle-opacity': ['get', 'opacity'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });
      
      // Add pulse animation layer for new loads
      if (showingNewLoads) {
        map.addLayer({
          id: 'city-bubbles-pulse',
          type: 'circle',
          source: 'city-bubbles',
          filter: ['==', ['get', 'isNewLoad'], true],
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['get', 'loadCount'],
              1, 25,      // Slightly larger than main bubbles
              10, 40,     
              25, 55,     
              50, 70      
            ],
            'circle-color': '#10b981',
            'circle-opacity': 0.3,
            'circle-stroke-width': 0
          }
        });
        
        // Animate the pulse effect
        let pulseRadius = 25;
        let growing = true;
        
        const animatePulse = () => {
          if (growing) {
            pulseRadius += 2;
            if (pulseRadius >= 35) growing = false;
          } else {
            pulseRadius -= 2;
            if (pulseRadius <= 25) growing = true;
          }
          
          if (map.getLayer('city-bubbles-pulse')) {
            map.setPaintProperty('city-bubbles-pulse', 'circle-radius', [
              'interpolate',
              ['linear'],
              ['get', 'loadCount'],
              1, pulseRadius,
              10, pulseRadius + 15,
              25, pulseRadius + 30,
              50, pulseRadius + 45
            ]);
          }
        };
        
        // Start animation
        const pulseInterval = setInterval(animatePulse, 100);
        
        // Store interval reference to clean up later
        if (!map._pulseInterval) map._pulseInterval = [];
        map._pulseInterval.push(pulseInterval);
      }
    } else {
      console.log('🔄 City bubbles layer already exists');
      
      // Clean up pulse animations if switching away from new loads view
      if (!showingNewLoads && map._pulseInterval) {
        map._pulseInterval.forEach(interval => clearInterval(interval));
        map._pulseInterval = [];
        
        // Remove pulse layer
        if (map.getLayer('city-bubbles-pulse')) {
          map.removeLayer('city-bubbles-pulse');
        }
      }
    }

    // Add text labels only if they don't already exist
    if (!map.getLayer('city-labels')) {
      try {
        console.log('🏷️ Adding city-labels layer');
        map.addLayer({
          id: 'city-labels',
          type: 'symbol',
          source: 'city-bubbles',
          layout: {
            'text-field': ['get', 'displayValue'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 16,
            'text-anchor': 'center',
            'text-allow-overlap': true
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 2
          }
        });
        console.log('✅ City-labels layer added successfully');
      } catch (error) {
        console.error('❌ Error adding city-labels layer:', error);
      }
    } else {
      console.log('🔄 City labels layer already exists');
    }

    // Add click handlers
    map.on('click', 'city-bubbles', async (e) => {
      try {
        const properties = e.features[0].properties;
        const cityData = cities.find(city => city.cityState === properties.cityState);

        // Close any existing route popup
        if (routePopupRef.current) {
          routePopupRef.current.remove();
          routePopupRef.current = null;
        }

        // Close any existing hover popup
        if (currentPopupRef.current) {
          currentPopupRef.current.remove();
          currentPopupRef.current = null;
        }

        // Show detailed load information popup
        if (cityData && cityData.loads) {
          const loadsToShow = cityData.loads.slice(0, 5); // Show first 5 loads
          const hasMoreLoads = cityData.loads.length > 5;

          const loadDetailsHTML = loadsToShow.map(load => `
            <div class="border-b border-gray-200 pb-4 mb-4 last:border-b-0 last:pb-0 last:mb-0">
              <div class="flex justify-between items-start mb-3">
                <div class="font-medium text-gray-900 text-base">
                  ${formatDate(load.PickupDate)}
                </div>
                <div class="font-bold text-green-600 text-lg">
                  ${formatCurrency(load.PaymentAmount)}
                </div>
              </div>

              <div class="text-base text-gray-700 mb-3">
                <div class="flex items-center">
                  <span class="font-medium">${load.OriginCity}, ${load.OriginState}</span>
                  <span class="mx-2 text-gray-400">→</span>
                  <span class="font-medium">${load.DestinationCity}, ${load.DestinationState}</span>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3 text-sm text-gray-600 mb-3">
                <div><span class="font-medium">Rate/Mile:</span> $${load.RatePerMile.toFixed(2)}</div>
                <div><span class="font-medium">Miles:</span> ${load.Mileage}</div>
              </div>

              <div class="text-sm text-gray-600 mb-3">
                <div class="mb-1"><span class="font-medium">Broker:</span> ${load.BrokerName || 'N/A'}</div>
                ${load.Email ? `<div class="mb-1"><span class="font-medium">Email:</span> ${load.Email}</div>` : ''}
                ${load.Phone ? `<div><span class="font-medium">Phone:</span> ${load.Phone}</div>` : ''}
              </div>

              ${load.SpecInfo && load.SpecInfo.trim() ? `
                <div class="text-sm bg-gray-50 rounded p-3 max-h-20 overflow-y-auto">
                  <span class="font-medium text-gray-700">Spec Info:</span>
                  <div class="mt-1 text-gray-600 leading-relaxed">${load.SpecInfo.length > 120 ?
                    load.SpecInfo.substring(0, 120) + '...' : load.SpecInfo}</div>
                </div>
              ` : ''}
            </div>
          `).join('');

          currentPopupRef.current = new window.mapboxgl.Popup({
            closeButton: true,
            closeOnClick: false, // Don't close on click outside
            closeOnMove: false,  // Don't close on map movement
            offset: 25,
            maxWidth: '500px'    // Increased to accommodate larger font sizes
          })
            .setLngLat(e.lngLat)
            .setHTML(`
              <div class="p-5 bg-white rounded-lg shadow-lg border max-w-md">
                <div class="font-bold text-xl text-gray-900 mb-4 flex items-center justify-between">
                  <span>${properties.city}, ${properties.state}</span>
                  <span class="text-base font-normal bg-blue-100 text-blue-800 px-3 py-1 rounded">
                    ${cityData.loads.length} load${cityData.loads.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div class="max-h-80 overflow-y-auto">
                  ${loadDetailsHTML}
                </div>

                ${hasMoreLoads ? `
                  <div class="mt-4 text-sm text-gray-500 italic border-t pt-3">
                    Showing ${loadsToShow.length} of ${cityData.loads.length} loads. Click "View All" below for complete list.
                  </div>
                ` : ''}

                <div class="mt-4 pt-3 border-t">
                  <button
                    onclick="window.showAllLoads('${cityData.cityState}')"
                    class="w-full text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded transition-colors"
                  >
                    View All Loads
                  </button>
                </div>
              </div>
            `)
            .addTo(map);

          // Add close event listener for logging
          currentPopupRef.current.on('close', () => {
            console.log('🗑️ Persistent popup closed by user');
          });
        }

        // Add driving route visualization using actual coordinates
        if (searchOrigin && cityData) {
          // Get origin coordinates using Mapbox geocoding
          const originCoords = await getCityCoordinates(searchOrigin);

          // Use actual destination coordinates from the city data
          const destCoords = [cityData.lon, cityData.lat];

          await addDrivingRoute(
            map,
            originCoords,
            destCoords,
            properties.cityState
          );
        }

        // Still call the original click handler for the data table
        if (cityData && onCityClick) {
          onCityClick(cityData);
        }
      } catch (error) {
        console.error('Error handling city bubble click:', error);
      }
    });

    // Add hover effects
    map.on('mouseenter', 'city-bubbles', (e) => {
      try {
        map.getCanvas().style.cursor = 'pointer';

        // Don't interfere with persistent popups (they have close buttons)
        if (currentPopupRef.current && currentPopupRef.current._closeButton) {
          return; // This is a persistent popup, don't remove it
        }

        // Remove any existing hover popup first
        if (currentPopupRef.current) {
          currentPopupRef.current.remove();
          currentPopupRef.current = null;
        }
        
        const properties = e.features[0].properties;
        
        // Create hover popup
        currentPopupRef.current = new window.mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 25
        })
          .setLngLat(e.lngLat)
          .setHTML(`
            <div class="p-3 bg-white rounded-lg shadow-lg border min-w-48">
              <div class="font-bold text-lg text-gray-900 mb-2">${properties.city}, ${properties.state}</div>
              <div class="space-y-1 text-sm">
                <div><span class="font-medium">Distance:</span> ${Math.round(properties.avgDistance)} miles</div>
                <div><span class="font-medium">Rate per mile:</span> $${properties.avgRate.toFixed(2)}</div>
                <div><span class="font-medium">Loads available:</span> ${properties.loadCount}</div>
                <div><span class="font-medium text-green-600">Avg Payment:</span> <span class="font-bold text-green-600">${formatCurrency(properties.avgPayment)}</span></div>
                <div><span class="font-medium text-purple-600">Avg Profit:</span> <span class="font-bold text-purple-600">${formatCurrency(properties.avgProfit)}</span></div>
                ${properties.avgPriceDiff !== null && properties.avgPriceDiff !== undefined && !isNaN(properties.avgPriceDiff) ?
                  `<div><span class="font-medium text-blue-600">Avg Price Diff:</span> <span class="font-bold text-blue-600">${formatPriceDiffForBubble(properties.avgPriceDiff)}</span></div>` : ''}
              </div>
              <div class="mt-2 text-xs text-gray-500 italic">Click to view all loads</div>
            </div>
          `)
          .addTo(map);
      } catch (error) {
        console.error('Error handling city bubble hover:', error);
      }
    });

    map.on('mouseleave', 'city-bubbles', (e) => {
      try {
        map.getCanvas().style.cursor = '';

        // Don't remove persistent popup on mouse leave (they have close buttons)
        if (currentPopupRef.current && currentPopupRef.current._closeButton) {
          return; // This is a persistent popup, don't remove it
        }

        // Remove hover popup
        if (currentPopupRef.current) {
          currentPopupRef.current.remove();
          currentPopupRef.current = null;
        }
      } catch (error) {
        console.error('Error handling city bubble mouse leave:', error);
      }
    });

    console.log('✅ City bubbles added successfully');
  };

  // Add search origin marker to map
  const addSearchOriginMarker = async (map, originCity) => {
    console.log('🎯 Adding search origin marker for:', originCity);
    
    // Clear existing origin marker - more robust cleanup
    try {
      // Remove layers first (order matters)
      if (map.getLayer('origin-label')) {
        console.log('🗑️ Removing existing origin-label layer');
        map.removeLayer('origin-label');
      }
    } catch (e) {
      console.log('No origin-label layer to remove:', e.message);
    }
    
    try {
      if (map.getLayer('origin-marker')) {
        console.log('🗑️ Removing existing origin-marker layer');
        map.removeLayer('origin-marker');
      }
    } catch (e) {
      console.log('No origin-marker layer to remove:', e.message);
    }
    
    try {
      // Then remove source
      if (map.getSource('origin-marker')) {
        console.log('🗑️ Removing existing origin-marker source');
        map.removeSource('origin-marker');
      }
    } catch (e) {
      console.log('No origin-marker source to remove:', e.message);
    }

    if (!originCity) {
      console.log('❌ No origin city provided, skipping marker');
      return;
    }

    const originCoords = await getCityCoordinates(originCity);
    console.log('📍 Origin coordinates for', originCity, ':', originCoords);
    
    // Add origin marker source only if it doesn't already exist
    if (!map.getSource('origin-marker')) {
      map.addSource('origin-marker', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: originCoords
          },
          properties: {
            city: originCity,
            type: 'origin'
          }
        }
      });
    } else {
      // Update existing source data
      console.log('🔄 Updating existing origin marker source data');
      map.getSource('origin-marker').setData({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: originCoords
        },
        properties: {
          city: originCity,
          type: 'origin'
        }
      });
    }

    // Add origin marker layer only if it doesn't already exist
    if (!map.getLayer('origin-marker')) {
      console.log('➕ Adding origin-marker layer');
      map.addLayer({
        id: 'origin-marker',
        type: 'circle',
        source: 'origin-marker',
        paint: {
          'circle-radius': 20,
          'circle-color': '#3b82f6', // Blue
          'circle-opacity': 0.9,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        }
      });
    } else {
      console.log('🔄 Origin marker layer already exists');
    }

    // Add origin label layer only if it doesn't already exist
    if (!map.getLayer('origin-label')) {
      console.log('➕ Adding origin-label layer');
      map.addLayer({
        id: 'origin-label',
        type: 'symbol',
        source: 'origin-marker',
        layout: {
          'text-field': '📍 ' + originCity,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 1.5],
          'text-allow-overlap': true
        },
        paint: {
          'text-color': '#1f2937',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2
        }
      });
    } else {
      console.log('🔄 Origin label layer already exists');
    }
    
    console.log('✅ Origin marker and label layers added successfully');

    // Add hover effect for origin marker
    map.on('mouseenter', 'origin-marker', () => {
      map.getCanvas().style.cursor = 'pointer';
      
      // Remove any existing popup first
      if (currentPopupRef.current) {
        currentPopupRef.current.remove();
        currentPopupRef.current = null;
      }
      
      currentPopupRef.current = new window.mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 25
      })
        .setLngLat(originCoords)
        .setHTML(`
          <div class="p-3 bg-blue-50 rounded-lg shadow-lg border border-blue-200 min-w-32">
            <div class="font-bold text-sm text-blue-900 mb-1">📍 Search Origin</div>
            <div class="text-blue-700 font-medium">${originCity}</div>
            <div class="text-xs text-blue-600 mt-1 italic">Your pickup location</div>
          </div>
        `)
        .addTo(map);
    });

    map.on('mouseleave', 'origin-marker', () => {
      map.getCanvas().style.cursor = '';
      
      if (currentPopupRef.current) {
        currentPopupRef.current.remove();
        currentPopupRef.current = null;
      }
    });

    console.log('✅ Search origin marker added for:', originCity);
  };

  // Find closest city from coordinates using Mapbox reverse geocoding
  const findClosestCity = async (clickLat, clickLng) => {
    try {
      const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
      if (!MAPBOX_ACCESS_TOKEN) {
        console.warn('No Mapbox token available for reverse geocoding');
        return null;
      }

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${clickLng},${clickLat}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=US&types=place&limit=1`
      );
      
      if (!response.ok) {
        throw new Error(`Reverse geocoding API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const cityName = feature.text;
        
        // Try to find state from context
        let stateName = '';
        if (feature.context) {
          const stateContext = feature.context.find(ctx => ctx.id.startsWith('region'));
          if (stateContext && stateContext.short_code) {
            stateName = stateContext.short_code.replace('US-', '');
          }
        }
        
        if (cityName && stateName) {
          const cityState = `${cityName}, ${stateName}`;
          console.log(`📍 Reverse geocoded ${clickLat}, ${clickLng} to ${cityState}`);
          return cityState;
        }
      }
      
      console.warn(`No city found for coordinates ${clickLat}, ${clickLng}`);
      return null;
      
    } catch (error) {
      console.error('Error reverse geocoding coordinates:', error);
      
      // Fallback to hardcoded city lookup if Mapbox fails
      const cityCoords = {
        'Chicago, IL': [-87.6298, 41.8781],
        'Los Angeles, CA': [-118.2437, 34.0522],
        'New York, NY': [-74.0059, 40.7128],
        'Houston, TX': [-95.3698, 29.7604],
        'Phoenix, AZ': [-112.0740, 33.4484],
        'Philadelphia, PA': [-75.1652, 39.9526],
        'San Antonio, TX': [-98.4936, 29.4241],
        'San Diego, CA': [-117.1611, 32.7157],
        'Dallas, TX': [-96.7970, 32.7767],
        'San Jose, CA': [-121.8863, 37.3382],
        'Austin, TX': [-97.7431, 30.2672],
        'Jacksonville, FL': [-81.6557, 30.3322],
        'Fort Worth, TX': [-97.3301, 32.7555],
        'Columbus, OH': [-82.9988, 39.9612],
        'Indianapolis, IN': [-86.1581, 39.7684],
        'Charlotte, NC': [-80.8431, 35.2271],
        'San Francisco, CA': [-122.4194, 37.7749],
        'Seattle, WA': [-122.3321, 47.6062],
        'Denver, CO': [-104.9903, 39.7392],
        'Washington, DC': [-77.0369, 38.9072],
        'Boston, MA': [-71.0589, 42.3601],
        'El Paso, TX': [-106.4850, 31.7619],
        'Detroit, MI': [-83.0458, 42.3314],
        'Nashville, TN': [-86.7816, 36.1627],
        'Portland, OR': [-122.6784, 45.5152],
        'Memphis, TN': [-90.0490, 35.1495],
        'Oklahoma City, OK': [-97.5164, 35.4676],
        'Las Vegas, NV': [-115.1398, 36.1699],
        'Louisville, KY': [-85.7585, 38.2527],
        'Baltimore, MD': [-76.6122, 39.2904],
        'Milwaukee, WI': [-87.9065, 43.0389],
        'Atlanta, GA': [-84.3880, 33.7490],
        'Miami, FL': [-80.1918, 25.7617],
        'Kansas City, MO': [-94.5786, 39.0997],
        'Tampa, FL': [-82.4572, 27.9506],
        'Cleveland, OH': [-81.6944, 41.4993],
        'New Orleans, LA': [-90.0715, 29.9511],
        'Minneapolis, MN': [-93.2650, 44.9778]
      };

      let closestCity = null;
      let minDistance = Infinity;

      // Calculate distance using simple Euclidean distance (good enough for city lookup)
      Object.entries(cityCoords).forEach(([cityName, [lng, lat]]) => {
        const distance = Math.sqrt(
          Math.pow(lat - clickLat, 2) + Math.pow(lng - clickLng, 2)
        );
        
        if (distance < minDistance) {
          minDistance = distance;
          closestCity = cityName;
        }
      });

      // Only return city if it's reasonably close (within ~200 miles / ~3 degrees)
      if (minDistance < 3.0) {
        return closestCity;
      }
      
      return null;
    }
  };

  // Add right-click context menu
  const addContextMenu = (map) => {
    let contextMenuDiv = null;

    // Remove existing context menu
    const removeContextMenu = () => {
      if (contextMenuDiv) {
        document.body.removeChild(contextMenuDiv);
        contextMenuDiv = null;
      }
    };

    // Create context menu
    const createContextMenu = (e, coords) => {
      removeContextMenu();

      const [lng, lat] = coords;
      
      contextMenuDiv = document.createElement('div');
      contextMenuDiv.className = 'fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-2 min-w-48';
      contextMenuDiv.style.left = e.point.x + 'px';
      contextMenuDiv.style.top = e.point.y + 'px';
      contextMenuDiv.style.position = 'fixed';
      
      const menuItems = [
        {
          label: '📍 Copy Coordinates',
          action: () => {
            navigator.clipboard.writeText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            console.log('📋 Copied coordinates:', `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            removeContextMenu();
          }
        },
        {
          label: '🔍 Zoom Here',
          action: () => {
            map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 8) });
            removeContextMenu();
          }
        },
        {
          label: '🗺️ Set as Origin',
          action: async () => {
            console.log('🗺️ Set as Origin clicked!');
            console.log('🗺️ onSetOrigin callback available:', typeof onSetOrigin);
            
            // Find closest city using Mapbox reverse geocoding
            const closestCity = await findClosestCity(lat, lng);
            console.log('🗺️ Closest city found:', closestCity);
            
            if (closestCity && onSetOrigin) {
              console.log('🎯 Setting new origin to city:', closestCity);
              onSetOrigin(closestCity);
            } else if (onSetOrigin) {
              // Fallback to coordinates if no city found
              const coordString = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
              console.log('🎯 No city found, using coordinates:', coordString);
              onSetOrigin(coordString);
            } else {
              console.error('❌ onSetOrigin callback not available');
            }
            removeContextMenu();
          }
        },
        {
          label: '📱 Directions',
          action: () => {
            const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
            window.open(url, '_blank');
            removeContextMenu();
          }
        },
        {
          label: '🔄 Refresh Map',
          action: () => {
            map.resize();
            console.log('🔄 Map refreshed');
            removeContextMenu();
          }
        }
      ];

      menuItems.forEach(item => {
        const menuItem = document.createElement('div');
        menuItem.className = 'px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-700 dark:text-gray-300';
        menuItem.textContent = item.label;
        menuItem.onclick = item.action;
        contextMenuDiv.appendChild(menuItem);
      });

      document.body.appendChild(contextMenuDiv);
    };

    // Right-click handler
    map.on('contextmenu', (e) => {
      e.preventDefault();
      createContextMenu(e, [e.lngLat.lng, e.lngLat.lat]);
    });

    // Click anywhere to close context menu and clear route
    map.on('click', (e) => {
      removeContextMenu();
      
      // Clear route if clicking on empty space (not on a bubble)
      const features = map.queryRenderedFeatures(e.point, { layers: ['city-bubbles'] });
      if (features.length === 0) {
        try {
          // Clear route layers and sources
          if (map.getLayer('route-label')) {
            map.removeLayer('route-label');
          }
          if (map.getLayer('route')) {
            map.removeLayer('route');
          }
          if (map.getSource('route-label')) {
            map.removeSource('route-label');
          }
          if (map.getSource('route')) {
            map.removeSource('route');
          }
          
          // Clear route popup if it exists
          if (routePopupRef.current) {
            routePopupRef.current.remove();
            routePopupRef.current = null;
          }
          
          console.log('🗺️ Route cleared');
        } catch (error) {
          // Route might not exist, that's ok
        }
      }
    });
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        removeContextMenu();
      }
    });

    console.log('✅ Right-click context menu added to map');
  };

  // Map initialization
  useEffect(() => {
    if (!isVisible || mapInstanceRef.current) return;

    const initializeMap = () => {
      if (!window.mapboxgl) {
        console.log('Mapbox GL JS not loaded');
        return;
      }

      // Check if Mapbox token is available
      const mapboxToken = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
      if (!mapboxToken) {
        console.error('❌ Mapbox API token not found. Map will not load.');
        console.log('💡 Please set REACT_APP_MAPBOX_PUBLIC_KEY environment variable');
        return;
      }

      console.log('🗺️ Initializing bubble map');
      
      // Determine map center and zoom based on search origin
      let mapCenter = [-95.7129, 37.0902]; // Default center of USA
      let mapZoom = 4; // Default zoom to see entire USA
      
      const map = new window.mapboxgl.Map({
        container: mapRef.current,
        style: getMapboxStyle(mapBackground),
        center: mapCenter,
        zoom: mapZoom,
        accessToken: mapboxToken,
        pitch: 0, // Flat top-down view (0 degrees) - matches MapResults.js
        bearing: 0, // Initial rotation - matches MapResults.js
        antialias: true // Smooth rendering - matches MapResults.js
      });

      map.on('load', async () => {
        console.log('✅ Bubble map loaded successfully');
        setMapLoaded(true);
        
        // Center map on search origin if provided
        if (searchOrigin) {
          const originCoords = await getCityCoordinates(searchOrigin);
          console.log('🎯 Centering bubble map on searched city:', searchOrigin, 'at coordinates:', originCoords);
          map.setCenter(originCoords);
        }
        
        // Add search origin marker
        if (searchOrigin) {
          await addSearchOriginMarker(map, searchOrigin);
        }
        
        // Add city bubbles if data available
        if (cityData && cityData.length > 0) {
          addCityBubbles(map, cityData, searchOrigin);
        }
        
        // Add right-click context menu
        addContextMenu(map);
      });

      map.on('error', (e) => {
        console.error('❌ Bubble map error:', e);
      });

      // Add navigation controls (commented out to hide sidebar controls - matches MapResults.js)
      // map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');

      mapInstanceRef.current = map;
    };

    // Load Mapbox if not already loaded
    if (!window.mapboxgl) {
      const script = document.createElement('script');
      script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
      script.onload = initializeMap;
      document.head.appendChild(script);
    } else {
      initializeMap();
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        setMapLoaded(false);
      }
    };
  }, [isVisible]);

  // Update bubbles when city data or display mode changes
  useEffect(() => {
    const updateMap = async () => {
      if (mapLoaded && mapInstanceRef.current) {
        console.log('🔄 Updating map with new data or display mode');
        
        // Add search origin marker
        if (searchOrigin) {
          await addSearchOriginMarker(mapInstanceRef.current, searchOrigin);
        }
        
        // Note: Popup will stay persistent and only close on manual close button or new search
        // This prevents the popup from disappearing when data changes (like Top Lanes toggle)

        // Add city bubbles if data available
        if (cityData && cityData.length > 0) {
          addCityBubbles(mapInstanceRef.current, cityData, searchOrigin);
        }

        // Add commentary markers if enabled
        addCommentaryMarkers(mapInstanceRef.current);
      }
    };

    updateMap();
  }, [cityData, mapLoaded, searchOrigin, displayMode, newLoads, showingNewLoads, showCommentary, bestRatedLane]);

  // Update map background when mapBackground changes
  useEffect(() => {
    if (mapLoaded && mapInstanceRef.current) {
      try {
        console.log('🎨 Updating map background to:', mapBackground);
        const styleUrl = getMapboxStyle(mapBackground);
        console.log('🎨 Using style URL:', styleUrl);
        
        mapInstanceRef.current.setStyle(styleUrl);
        
        // Re-add layers after style change (style changes remove all custom layers)
        mapInstanceRef.current.once('styledata', () => {
          console.log('🔄 Style updated, re-adding layers');
          
          // Add a small delay to ensure the style is fully loaded
          setTimeout(async () => {
            try {
              // Ensure the map is still loaded and ready
              if (!mapInstanceRef.current || !mapInstanceRef.current.isStyleLoaded()) {
                console.log('⏳ Map style not ready, waiting...');
                return;
              }
              
              console.log('✅ Map style ready, adding layers');
              
              // Add search origin marker
              if (searchOrigin) {
                await addSearchOriginMarker(mapInstanceRef.current, searchOrigin);
              }
              
              // Add city bubbles if data available  
              if (cityData && cityData.length > 0) {
                addCityBubbles(mapInstanceRef.current, cityData, searchOrigin);
              }
            } catch (layerError) {
              console.error('❌ Error re-adding layers after style change:', layerError);
              // Don't re-throw the error to prevent uncaught runtime errors
            }
          }, 100); // Small delay to ensure style is fully ready
        });
        
        // Handle style load errors
        mapInstanceRef.current.once('error', (e) => {
          console.error('❌ Error loading map style:', e);
          // Fallback to light style if there's an error
          if (mapBackground !== 'light') {
            console.log('🔄 Falling back to light style');
            mapInstanceRef.current.setStyle(getMapboxStyle('light'));
          }
        });
        
      } catch (error) {
        console.error('❌ Error updating map style:', error);
      }
    }
  }, [mapBackground]);

  // Clear routes when clearRoutes prop changes (triggered by new search)
  useEffect(() => {
    if (clearRoutes > 0 && mapLoaded && mapInstanceRef.current) {
      console.log('🔄 Clearing routes due to new search');
      clearRoute(mapInstanceRef.current);
    }
  }, [clearRoutes, mapLoaded]);

  // Handle fullscreen mode changes
  useEffect(() => {
    if (mapLoaded && mapInstanceRef.current) {
      // Use requestAnimationFrame for better timing coordination with DOM updates
      const resizeMap = () => {
        requestAnimationFrame(() => {
          try {
            if (mapInstanceRef.current) {
              mapInstanceRef.current.resize();
              console.log('🔄 Map resized for fullscreen toggle');

              // Force additional resizes with proper timing
              setTimeout(() => {
                if (mapInstanceRef.current) {
                  mapInstanceRef.current.resize();
                  console.log('🔄 Map second resize completed');
                }
              }, 50);

              setTimeout(() => {
                if (mapInstanceRef.current) {
                  mapInstanceRef.current.resize();
                  console.log('🔄 Map third resize completed');
                }
              }, 200);
            }
          } catch (error) {
            console.error('Error resizing map:', error);
          }
        });
      };

      // Wait for DOM changes to complete
      setTimeout(resizeMap, 100);
    }
  }, [isMapFullscreen, mapLoaded]);

  if (!isVisible) return null;

  // Show fallback if Mapbox token is missing
  if (!process.env.REACT_APP_MAPBOX_PUBLIC_KEY) {
    return (
      <div className="relative w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg" style={{ minHeight: '100%' }}>
        <div className="text-center p-8">
          <div className="text-6xl mb-4">🗺️</div>
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Map Unavailable
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Map visualization requires Mapbox configuration
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {cityData?.length || 0} destinations found
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full" style={{ minHeight: '100%' }}>
      <div
        ref={mapRef}
        className="w-full h-full"
        style={{ minHeight: '100%' }}
      />
    </div>
  );
}

export default BubbleMapResults;